import { Link } from "react-router-dom";
import "./privacy.css";

export default function Privacy() {
  return (
    <main className="Privacy">
      <Link to="/">Back to Winkd</Link>
      <h1>Privacy Policy</h1>
      <p>Last updated September 20, 2026</p>
      <p>
        Winkd is operated by Macaroni Studios LLC. This policy explains the
        information used to provide Winkd, including missed-connection posts,
        nearby browsing, accounts, and text chat.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>
          <strong>Account information:</strong> email address, account identifiers,
          username, and avatar choices. If you sign in with Apple or Google, our
          authentication provider receives the information supplied by that
          service, which may include your name, email address, and profile image.
          Phone numbers and profile photos associated with earlier versions may
          remain in existing account records.
        </li>
        <li>
          <strong>Location:</strong> coordinates you provide through location
          permission or by selecting a location, and locations attached to posts.
          These support nearby posts and location-based browsing. Coordinates
          may be precise. Location you include in a public post is visible to
          other users.
        </li>
        <li>
          <strong>Content and activity:</strong> posts, comments, text messages,
          message participants, likes, saves, blocks, reports, preferences, and
          information you send to support. Winkd chat is text only; it does not
          offer file, photo, video, or audio attachments.
        </li>
        <li>
          <strong>Device and usage information:</strong> app and device
          identifiers, notification tokens, app/OS versions, screens visited,
          feature interactions, timestamps, and technical request information.
          Analytics can be associated with your account ID.
        </li>
        <li>
          <strong>Diagnostics:</strong> crash reports, error details, stack
          traces, app/device context, and identifiers used to diagnose failures.
          The app's current error-reporting configuration disables session
          replay, screenshots, view-hierarchy capture, and automatic performance
          tracing.
        </li>
      </ul>

      <h2>How we use information</h2>
      <p>
        We use this information to authenticate accounts, display nearby
        content, operate posts and chat, deliver notifications you enable,
        enforce message limits and safety rules, respond to support requests,
        prevent abuse, and diagnose technical problems. Product analytics helps
        us understand feature use and improve Winkd.
      </p>
      <p>
        Winkd does not use the app data described here for targeted advertising
        across other companies' apps or websites, or share it with data brokers.
      </p>

      <h2>Who receives information</h2>
      <p>
        Public posts, comments, and public profile details are visible to other
        users. Messages are available to their participants and processed by our
        chat service. Do not include information in public content that you do
        not want others to see or copy.
      </p>
      <p>We use service providers to operate the app, including:</p>
      <ul>
        <li>Supabase for authentication, database services, and storage.</li>
        <li>Stream for text chat and message delivery.</li>
        <li>Resend for email sign-in codes.</li>
        <li>Expo and platform notification services for app delivery and notifications.</li>
        <li>PostHog for product analytics and diagnostic events.</li>
        <li>Sentry for crash and error reporting.</li>
        <li>Apple and Google for platform, sign-in, and map services where used.</li>
      </ul>
      <p>
        Providers process information needed for their services. Information may
        also be disclosed when required by law or necessary to investigate abuse,
        protect users, or secure the service. Processing and storage may occur
        outside your state, province, or country, including in the United States.
      </p>

      <h2>Your choices and account deletion</h2>
      <p>
        You can manage location, camera/photo access, and notifications in your
        device settings. Denying a permission may limit the corresponding
        feature. You can edit available profile settings, block or report users,
        and delete your account from <strong>Settings → Account → Delete Account</strong>.
      </p>
      <p>
        Account deletion removes or clears active profile details and associated
        app records and marks the chat account and its messages as deleted.
        It does not erase copies other users have made. Backups, service logs,
        analytics/diagnostic records, and records needed for security or legal
        obligations may remain under applicable retention processes; deletion
        is not an immediate purge of every service's storage.
      </p>
      <p>
        For help accessing, correcting, or deleting personal information,
        withdrawing consent where applicable, or asking about retention,
        contact <a href="mailto:anthony.stuvecke@winkd.com">anthony.stuvecke@winkd.com</a>.
        We may need to verify that a request relates to your account. Please do
        not send passwords or sign-in codes.
      </p>

      <h2>Security and retention</h2>
      <p>
        We use access controls and encrypted network connections to protect
        information. No service can guarantee absolute security. We retain
        information as needed to operate the service, handle support and safety
        matters, and meet applicable obligations. Different services and backup
        systems may have different retention periods.
      </p>

      <h2>Children</h2>
      <p>
        Winkd is not directed to children under 13. If you believe a child under
        13 has provided personal information, contact us so we can investigate
        and address it. The App Store's displayed age rating also applies to
        availability through that platform.
      </p>

      <h2>Changes and contact</h2>
      <p>
        We may update this policy as the service changes. The date above
        identifies the latest revision. Contact Macaroni Studios LLC about
        privacy at <a href="mailto:anthony.stuvecke@winkd.com">anthony.stuvecke@winkd.com</a>,
        or visit our <Link to="/support">support page</Link>.
      </p>
    </main>
  );
}
